import React, { useEffect } from 'react';
import '../styles/TermsOfService.css'; // Import the CSS file for styling

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const lastUpdated = 'March 15, 2026';

  return (
    <div className="terms-page">
      <div className="terms-container">
        {/* Page Header */}
        <div className="terms-header">
          <span className="terms-tag">Terms of Service</span>
          <h1 className="terms-title">Terms & Conditions</h1>
          <p className="terms-subtitle">
            Please read these terms carefully before using our services. By using our website and services, 
            you agree to these terms.
          </p>
          <div className="terms-meta">
            <span className="terms-meta-item">📅 Last Updated: {lastUpdated}</span>
            <span className="terms-meta-divider">|</span>
            <span className="terms-meta-item">📋 Effective: Immediately</span>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="terms-toc">
          <h3>Table of Contents</h3>
          <ul className="terms-toc-list">
            <li><a href="#section-1">1. Acceptance of Terms</a></li>
            <li><a href="#section-2">2. Services Provided</a></li>
            <li><a href="#section-3">3. Booking & Payments</a></li>
            <li><a href="#section-4">4. Cancellation Policy</a></li>
            <li><a href="#section-5">5. User Responsibilities</a></li>
            <li><a href="#section-6">6. Intellectual Property</a></li>
            <li><a href="#section-7">7. Limitation of Liability</a></li>
            <li><a href="#section-8">8. Warranties & Guarantees</a></li>
            <li><a href="#section-9">9. Indemnification</a></li>
            <li><a href="#section-10">10. Governing Law</a></li>
            <li><a href="#section-11">11. Changes to Terms</a></li>
            <li><a href="#section-12">12. Contact Us</a></li>
          </ul>
        </div>

        {/* Terms Content */}
        <div className="terms-content">
          {/* Section 1 */}
          <section id="section-1" className="terms-section">
            <h2 className="terms-section-title">1. Acceptance of Terms</h2>
            <p className="terms-section-text">
              By accessing and using PestControl Today's website and services, you agree to be bound by these 
              Terms of Service. If you do not agree to these terms, please do not use our website or services.
            </p>
            <p className="terms-section-text">
              These terms apply to all visitors, users, and customers who access or use our services. We reserve 
              the right to update or modify these terms at any time without prior notice.
            </p>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="terms-section">
            <h2 className="terms-section-title">2. Services Provided</h2>
            <p className="terms-section-text">
              PestControl Today provides professional pest control services including but not limited to:
            </p>
            <ul className="terms-section-list">
              <li>General pest control for common household pests</li>
              <li>Termite treatment and prevention</li>
              <li>Bed bug extermination</li>
              <li>Rodent removal and prevention</li>
              <li>Mosquito control</li>
              <li>Commercial pest control services</li>
              <li>Eco-friendly treatment options</li>
              <li>Preventive maintenance programs</li>
            </ul>
            <p className="terms-section-text">
              We reserve the right to refuse service to any customer at our discretion.
            </p>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="terms-section">
            <h2 className="terms-section-title">3. Booking & Payments</h2>
            <p className="terms-section-text">
              <strong>Booking Process:</strong> Customers can book services through our website, by phone, or via email. 
              All bookings are subject to availability and confirmation.
            </p>
            <ul className="terms-section-list">
              <li><strong>Pricing:</strong> Service prices are provided at the time of booking and may vary based on 
              the scope of work, property size, and location.</li>
              <li><strong>Payment:</strong> Payment is due upon completion of services unless otherwise agreed. We accept 
              major credit cards, debit cards, and cash.</li>
              <li><strong>Estimates:</strong> Any estimates provided are approximate and may change if additional work 
              is required during service.</li>
              <li><strong>Deposits:</strong> For large projects or commercial services, a deposit may be required at 
              the time of booking.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="terms-section">
            <h2 className="terms-section-title">4. Cancellation Policy</h2>
            <ul className="terms-section-list">
              <li><strong>24-Hour Notice:</strong> We require at least 24 hours notice for cancellations or 
              rescheduling.</li>
              <li><strong>Late Cancellations:</strong> Cancellations made less than 24 hours before scheduled service 
              may be subject to a cancellation fee.</li>
              <li><strong>No-Show:</strong> If our technicians arrive and cannot access the property, a no-show fee 
              may apply.</li>
              <li><strong>Emergency Services:</strong> Special cancellation terms may apply for emergency service calls.</li>
            </ul>
            <p className="terms-section-text">
              We understand that emergencies happen. Please contact us as soon as possible if you need to cancel or 
              reschedule.
            </p>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="terms-section">
            <h2 className="terms-section-title">5. User Responsibilities</h2>
            <p className="terms-section-text">
              As a customer, you agree to:
            </p>
            <ul className="terms-section-list">
              <li>Provide accurate and complete information when booking services</li>
              <li>Ensure safe access to your property for our technicians</li>
              <li>Secure pets and children during service visits</li>
              <li>Disclose any known pest issues or previous treatments</li>
              <li>Follow post-treatment instructions provided by our technicians</li>
              <li>Inform us of any allergies or sensitivities to treatment products</li>
              <li>Pay for services as agreed upon</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="terms-section">
            <h2 className="terms-section-title">6. Intellectual Property</h2>
            <p className="terms-section-text">
              All content on our website, including text, graphics, logos, images, and software, is the property of 
              PestControl Today and is protected by copyright and intellectual property laws.
            </p>
            <p className="terms-section-text">
              You may not:
            </p>
            <ul className="terms-section-list">
              <li>Reproduce, distribute, or modify any content without our prior written consent</li>
              <li>Use our trademarks or logos without permission</li>
              <li>Copy or replicate our website design or functionality</li>
              <li>Reverse engineer any software used on our website</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="terms-section">
            <h2 className="terms-section-title">7. Limitation of Liability</h2>
            <p className="terms-section-text">
              PestControl Today shall not be liable for:
            </p>
            <ul className="terms-section-list">
              <li>Any indirect, incidental, or consequential damages arising from our services</li>
              <li>Property damage caused by pests or pre-existing conditions</li>
              <li>Failure to eliminate pests if customers do not follow post-treatment instructions</li>
              <li>Any loss or damage resulting from unauthorized access to your information</li>
              <li>Services performed in areas with hazardous conditions not disclosed by the customer</li>
            </ul>
            <p className="terms-section-text">
              Our total liability shall not exceed the amount paid for services provided.
            </p>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="terms-section">
            <h2 className="terms-section-title">8. Warranties & Guarantees</h2>
            <p className="terms-section-text">
              We stand behind our work with the following guarantees:
            </p>
            <ul className="terms-section-list">
              <li><strong>Satisfaction Guarantee:</strong> If you're not satisfied with our service, we'll return to 
              address the issue at no additional cost within 30 days.</li>
              <li><strong>Workmanship Warranty:</strong> Our work is backed by a warranty against defects in 
              workmanship for a specified period.</li>
              <li><strong>Product Warranties:</strong> We use products that are EPA-registered and applied according 
              to label instructions.</li>
            </ul>
            <p className="terms-section-text">
              Guarantees are subject to terms and conditions, including following post-treatment care instructions.
            </p>
          </section>

          {/* Section 9 */}
          <section id="section-9" className="terms-section">
            <h2 className="terms-section-title">9. Indemnification</h2>
            <p className="terms-section-text">
              You agree to indemnify and hold PestControl Today harmless from any claims, damages, losses, or expenses 
              arising from:
            </p>
            <ul className="terms-section-list">
              <li>Your violation of these terms</li>
              <li>Your misuse of our services</li>
              <li>Your violation of any third-party rights</li>
              <li>Your violation of applicable laws or regulations</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section id="section-10" className="terms-section">
            <h2 className="terms-section-title">10. Governing Law</h2>
            <p className="terms-section-text">
              These terms shall be governed by and construed in accordance with the laws of the State of New York, 
              without regard to its conflict of law provisions.
            </p>
            <p className="terms-section-text">
              Any disputes arising from these terms or our services shall be resolved in the courts of New York County, 
              New York.
            </p>
          </section>

          {/* Section 11 */}
          <section id="section-11" className="terms-section">
            <h2 className="terms-section-title">11. Changes to Terms</h2>
            <p className="terms-section-text">
              We reserve the right to update or modify these terms at any time. Changes will be effective immediately 
              upon posting to our website.
            </p>
            <p className="terms-section-text">
              We will notify users of material changes via email or through a notice on our website. Continued use 
              of our services after changes constitutes acceptance of the new terms.
            </p>
          </section>

          {/* Section 12 */}
          <section id="section-12" className="terms-section">
            <h2 className="terms-section-title">12. Contact Us</h2>
            <p className="terms-section-text">
              If you have any questions or concerns about these terms, please contact us:
            </p>
            <div className="terms-contact-info">
              <div className="terms-contact-item">
                <span className="terms-contact-icon">📞</span>
                <div>
                  <strong>Phone:</strong>
                  <a href="tel:+15551234567" className="terms-contact-link">(555) 123-4567</a>
                </div>
              </div>
              <div className="terms-contact-item">
                <span className="terms-contact-icon">✉️</span>
                <div>
                  <strong>Email:</strong>
                  <a href="mailto:legal@pestcontroltoday.com" className="terms-contact-link">legal@pestcontroltoday.com</a>
                </div>
              </div>
              <div className="terms-contact-item">
                <span className="terms-contact-icon">📍</span>
                <div>
                  <strong>Address:</strong>
                  <span className="terms-contact-text">123 Pest Free Lane, Suite 200, New York, NY 10001</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Back to Top */}
        <div className="terms-back-top">
          <button 
            className="terms-back-top-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            ↑ Back to Top
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;